import React, { useState } from 'react';
import { Search, SlidersHorizontal, ChevronRight, UserX, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  BUCKET_ORDER, BUCKET_META, fmtXof, agingFractions, initials, avatarColor,
} from './recMeta';

const MiniBar = ({ bucketTotals }) => (
  <div className="mt-1.5 flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
    {agingFractions(bucketTotals).map(({ k, pct }) => (
      <div key={k} className={BUCKET_META[k].bar} style={{ width: `${pct}%` }} />
    ))}
  </div>
);

// Zone gauche : vue équipe (commerciaux + clients non assignés + total).
export const TeamList = ({ team, selectedKey, onSelect, onClose }) => {
  const [q, setQ] = useState('');
  const commercials = (team?.commercials || []).filter(
    (c) => c.commercialName.toLowerCase().includes(q.toLowerCase()),
  );
  const un = team?.unassigned;

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm" data-testid="rec-team-list">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <h2 className="text-base font-semibold tracking-tight text-gray-900">Vue équipe</h2>
        {onClose && (
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un commercial…" className="h-8 pl-8 text-xs" data-testid="rec-team-search" />
        </div>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled title="Filtres (à venir)">
          <SlidersHorizontal className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="max-h-[640px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white">
            <tr className="text-[10px] uppercase tracking-wide text-gray-400">
              <th className="px-4 py-2 text-left font-medium">Commercial</th>
              <th className="px-2 py-2 text-right font-medium">Encours</th>
              <th className="px-2 py-2 text-center font-medium">Fact.</th>
              <th className="px-4 py-2 text-right font-medium">+90j</th>
            </tr>
          </thead>
          <tbody>
            {commercials.map((c) => {
              const active = selectedKey === c.commercialId;
              return (
                <tr
                  key={c.commercialId}
                  onClick={() => onSelect({ type: 'commercial', id: c.commercialId, name: c.commercialName })}
                  data-testid={`rec-team-row-${c.commercialId}`}
                  className={`cursor-pointer border-t border-gray-50 transition-colors ${active ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${avatarColor(c.commercialId)}`}>{initials(c.commercialName)}</span>
                      <div className="min-w-0">
                        <div className={`truncate font-medium ${active ? 'text-emerald-800' : 'text-gray-800'}`}>{c.commercialName}</div>
                        <MiniBar bucketTotals={c.bucketTotals} />
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-3 text-right font-semibold tabular-nums text-gray-800">{fmtXof(c.totalOutstanding)}</td>
                  <td className="px-2 py-3 text-center tabular-nums text-gray-500">{c.count}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <span className={`tabular-nums ${(c.bucketTotals['90+'] || 0) > 0 ? 'font-semibold text-red-600' : 'text-gray-400'}`}>{fmtXof(c.bucketTotals['90+'])}</span>
                      <ChevronRight className={`h-4 w-4 ${active ? 'text-emerald-600' : 'text-gray-300'}`} />
                    </div>
                  </td>
                </tr>
              );
            })}

            {/* Clients non assignés (anomalie) */}
            {un && (
              <tr
                onClick={() => onSelect({ type: 'unassigned', id: 'unassigned', name: 'Clients non assignés' })}
                data-testid="rec-team-row-unassigned"
                className={`cursor-pointer border-t border-gray-50 transition-colors ${selectedKey === 'unassigned' ? 'bg-amber-100/70' : 'bg-amber-50 hover:bg-amber-100/60'}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-400 text-white"><UserX className="h-4 w-4" /></span>
                    <div className="min-w-0">
                      <div className="truncate font-medium text-amber-900">Clients non assignés</div>
                      <MiniBar bucketTotals={un.bucketTotals} />
                    </div>
                  </div>
                </td>
                <td className="px-2 py-3 text-right font-semibold tabular-nums text-amber-900">{fmtXof(un.totalOutstanding)}</td>
                <td className="px-2 py-3 text-center tabular-nums text-amber-800">{un.count}</td>
                <td className="px-4 py-3 text-right font-semibold tabular-nums text-amber-900">{fmtXof(un.bucketTotals['90+'])}</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-100 bg-gray-50 font-bold">
              <td className="px-4 py-3 text-gray-700">Total équipe</td>
              <td className="px-2 py-3 text-right tabular-nums text-gray-900">{fmtXof(team?.totals?.totalOutstanding)}</td>
              <td className="px-2 py-3 text-center tabular-nums text-gray-700">{team?.totals?.count}</td>
              <td className="px-4 py-3 text-right tabular-nums text-red-700">{fmtXof(team?.totals?.bucketTotals?.['90+'])}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default TeamList;
